import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../layout/header";
import { isLoggedInAdmin } from "../libraries/auth";
import { getDefaultAccessibleRoute } from "../libraries/privileges";
import {
  clearAllTrashedTabs,
  getTrashedTabs,
  permanentlyDeleteTabFromTrash,
  restoreTabFromTrash,
  TRASHED_TABS_CHANGED_EVENT
} from "../libraries/trashedTabsStorage";

function formatTrashedAt(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function TrashPage() {
  const navigate = useNavigate();
  const [trashedTabs, setTrashedTabs] = useState([]);

  useEffect(() => {
    if (!isLoggedInAdmin()) {
      navigate(getDefaultAccessibleRoute(), { replace: true });
    }
  }, [navigate]);

  const refresh = useCallback(() => {
    setTrashedTabs(getTrashedTabs());
  }, []);

  useEffect(() => {
    if (!isLoggedInAdmin()) {
      return undefined;
    }
    refresh();
    window.addEventListener(TRASHED_TABS_CHANGED_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(TRASHED_TABS_CHANGED_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [refresh]);

  const handleRestore = (tabId) => {
    restoreTabFromTrash(tabId);
    refresh();
  };

  const handleDelete = (tabId) => {
    permanentlyDeleteTabFromTrash(tabId);
    refresh();
  };

  const handleClearAll = () => {
    if (!trashedTabs.length) return;
    if (!window.confirm("Permanently remove all tabs from trash?")) return;
    clearAllTrashedTabs();
    refresh();
  };

  const handleOpenBranch = (tab) => {
    if (!tab.branchId) return;
    navigate(`/branches/${tab.branchId}`, {
      state: {
        branchId: tab.branchId,
        activeRoute: tab.id
      }
    });
  };

  return (
    <div className="main">
      <Header />
      <div className="trash-page">
        <div className="trash-page-header">
          <div>
            <h1>Trash</h1>
            <p>Tabs moved from branch details appear here. Restore to show them again in the branch menu.</p>
          </div>
          {trashedTabs.length > 0 && (
            <button type="button" className="trash-clear-btn" onClick={handleClearAll}>
              Empty trash
            </button>
          )}
        </div>

        {trashedTabs.length === 0 ? (
          <div className="trash-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <polyline points="3 6 5 6 21 6"></polyline>
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              <line x1="10" y1="11" x2="10" y2="17"></line>
              <line x1="14" y1="11" x2="14" y2="17"></line>
            </svg>
            <h2>No tabs in trash</h2>
            <p>Use the trash icon on a branch tab to move it here.</p>
          </div>
        ) : (
          <div className="trash-list">
            {trashedTabs.map((tab) => (
              <div key={`${tab.id}-${tab.trashedAt}`} className="trash-item">
                <div className="trash-item-main">
                  <h3>{tab.label}</h3>
                  <p>
                    {tab.branchName
                      ? `From branch: ${tab.branchName}`
                      : "Branch tab"}
                    {" · "}
                    Moved {formatTrashedAt(tab.trashedAt)}
                  </p>
                </div>
                <div className="trash-item-actions">
                  {tab.branchId && (
                    <button
                      type="button"
                      className="trash-action-btn secondary"
                      onClick={() => handleOpenBranch(tab)}
                    >
                      Open branch
                    </button>
                  )}
                  <button
                    type="button"
                    className="trash-action-btn primary"
                    onClick={() => handleRestore(tab.id)}
                  >
                    Restore
                  </button>
                  <button
                    type="button"
                    className="trash-action-btn danger"
                    onClick={() => handleDelete(tab.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default TrashPage;
